/* ==========================================================================
   Billing Types — matching docs/api-specification.md (Billing section)

   Os campos de plano (change billing-plans) são acréscimos: uma API anterior
   não os manda, e o painel trata a ausência como antes dos planos (sem
   escolha de plano, faturas como "Assinatura", sem card de mudança).
   ========================================================================== */

// --- Plans ---

/**
 * Código estável do plano. Os conhecidos são `essential`, `attendant` e
 * `trial`; o tipo fica aberto porque a lista à venda vem da API, e um plano
 * novo não pode quebrar o painel antes de ele ser atualizado.
 */
export type PlanCode = 'essential' | 'attendant' | 'trial' | (string & {});

export const PLAN_CODES = {
  essential: 'essential',
  attendant: 'attendant',
  trial: 'trial',
} as const;

/** Um plano à venda (`plans[]` de `GET /billing/pricing`). */
export interface PricingPlan {
  code: PlanCode;
  name: string;
  monthlyPrice: number;
  yearlyPrice: number;
  includedAiUnitsPerMonth: number;
  features: string[];
}

/**
 * Um pacote de atendimentos à venda (`aiUnitPacks[]` de `GET /billing/pricing`,
 * change billing-ai-units), com o retrato da versão vigente.
 */
export interface AiUnitPack {
  /** Código estável (`units_300`). */
  code: string;
  units: number;
  /** Preço em BRL. */
  price: number;
  /** Validade em meses, contada do pagamento. */
  validityMonths: number;
  /** Preço por atendimento, com quatro casas. */
  unitPrice: number;
}

// --- Pricing (GET /billing/pricing) ---

export interface PricingInfo {
  /** Preço do Essencial, mantido por compatibilidade. */
  monthlyPrice: number;
  /** Preço do Essencial, mantido por compatibilidade. */
  yearlyPrice: number;
  currency?: string;
  /** Planos à venda, na ordem do catálogo. Ausente numa API anterior aos planos. */
  plans?: PricingPlan[];
  /**
   * Pacotes de atendimentos à venda, por quantidade (change billing-ai-units).
   * Ausente numa API anterior aos pacotes, e vazio com o banco fora.
   */
  aiUnitPacks?: AiUnitPack[];
}

// --- Enums ---

export type BillingCycle = 'monthly' | 'yearly';

export type PaymentMethod = 'credit_card' | 'boleto' | 'pix';

export type PersonType = 'pf' | 'pj';

export type SubscriptionStatus =
  | 'none'
  | 'pending'
  | 'active'
  | 'overdue'
  | 'cancelled';

export type InvoiceStatus = 'paid' | 'pending' | 'overdue' | 'refunded' | 'cancelled';

export type InvoiceKind = 'subscription' | 'plan_change' | 'ai_units';

export type CancellationStatus =
  | 'processing'
  | 'reconciliation_pending'
  | 'completed'
  | 'failed_retryable';

export type PlanChangeKind = 'upgrade' | 'downgrade';

export type PlanChangeStatus =
  | 'awaiting_payment'
  | 'applying'
  | 'scheduled'
  | 'completed'
  | 'failed_retryable'
  | 'cancelled';

/** Só os estados abertos aparecem em `pendingPlanChange`. */
export type OpenPlanChangeStatus = Extract<
  PlanChangeStatus,
  'awaiting_payment' | 'applying' | 'scheduled' | 'failed_retryable'
>;

/** Formas de pagamento da diferença do upgrade: boleto leva dias, e o upgrade libera na confirmação. */
export type PlanChangePaymentMethod = Extract<PaymentMethod, 'pix' | 'credit_card'>;

// --- Subscription (GET /billing/subscriptions/current) ---

/** A mudança de plano aberta da assinatura. */
export interface PendingPlanChange {
  kind: PlanChangeKind;
  status: OpenPlanChangeStatus;
  toPlanCode: PlanCode;
  toPlanName: string;
  /** Diferença do upgrade; 0 no downgrade e no upgrade sem cobrança. */
  prorationAmount: number;
  /** Upgrade: o instante do pagamento (nulo enquanto aguarda). Downgrade: o início do período ainda não pago. */
  effectiveAt: string | null;
  paymentDueDate: string | null;
  paymentMethod: PlanChangePaymentMethod | null;
  paymentUrl: string | null;
}

export interface CurrentSubscription {
  status: SubscriptionStatus;
  billingCycle?: BillingCycle;
  nextDueDate?: string;
  paymentMethod?: PaymentMethod;
  createdAt?: string;
  paymentUrl?: string | null;
  cancellationStatus?: CancellationStatus | null;
  cancellationRequestedAt?: string | null;
  accessEndsAt?: string | null;
  /** Plano da assinatura. Ausente numa API anterior aos planos. */
  planCode?: PlanCode | null;
  planName?: string | null;
  /** O que o gateway cobra a cada ciclo, com o desconto comercial ativo. */
  value?: number | null;
  pendingPlanChange?: PendingPlanChange | null;
}

export interface CancelSubscriptionRequest {
  reason: string;
  idempotencyKey: string;
}

export interface CancelSubscriptionResponse {
  subscriptionId: string;
  status: 'cancelled';
  cancellationStatus: CancellationStatus;
  cancellationRequestedAt: string;
  accessEndsAt: string | null;
}

// --- Create subscription (POST /billing/subscriptions) ---

export interface BillingInfo {
  personType: PersonType;
  name: string;
  email: string;
  phone: string;
  document: string;
  zipCode: string;
}

export interface CreateSubscriptionRequest {
  billingCycle: BillingCycle;
  paymentMethod: PaymentMethod;
  billingInfo: BillingInfo;
  /** Só com mais de um plano à venda; ausente, a API assina o Essencial. */
  planCode?: PlanCode;
}

export interface CreateSubscriptionResponse {
  subscriptionId: string;
  asaasSubscriptionId: string;
  status: SubscriptionStatus;
  paymentUrl: string | null;
  createdAt: string;
}

// --- Entitlements (GET /billing/entitlements) ---

export type EntitlementSource = 'none' | 'trial' | 'subscription' | 'override';

/**
 * Direitos do tenant. Área do painel condicionada a um recurso do plano lê
 * `features`, nunca o nome do plano.
 */
export interface TenantEntitlements {
  /** `null` só com `source: "none"`. */
  planCode: PlanCode | null;
  features: string[];
  source: EntitlementSource;
  /** Fim (exclusivo) da cortesia; só com `source: "override"`. */
  overrideEndsAt: string | null;
}

export const TENANT_FEATURE_KEYS = {
  whatsappAttendant: 'whatsapp_attendant',
} as const;

// --- Plan change (/billing/subscriptions/current/plan-change) ---

export interface PlanChangeQuote {
  kind: PlanChangeKind;
  toPlanCode: PlanCode;
  toPlanName: string;
  /** O que se paga agora; 0 no downgrade e abaixo do mínimo de cobrança. */
  prorationAmount: number;
  chargeRequired: boolean;
  remainingDays: number;
  cycleDays: number;
  /** Ciclos inteiros já pagos adiante, que entram inteiros na diferença. */
  prepaidCycles: number;
  /** O que o gateway passa a cobrar por ciclo no plano novo, com o desconto ativo. */
  newCycleValue: number;
  /** Downgrade: quando o plano novo passa a valer. Upgrade: nulo (vale no pagamento). */
  effectiveAt: string | null;
}

export interface PlanChangePix {
  /** PNG em base64. */
  encodedImage: string;
  /** Copia-e-cola. */
  payload: string;
  expiresAt: string | null;
}

export interface PlanChangeResult extends PlanChangeQuote {
  planChangeId: string;
  status: PlanChangeStatus;
  paymentDueDate: string | null;
  paymentMethod: PlanChangePaymentMethod | null;
  paymentUrl: string | null;
  pix: PlanChangePix | null;
}

export interface PlanChangeRequest {
  toPlanCode: PlanCode;
  /** Exigido só quando a cotação diz que há cobrança. */
  paymentMethod?: PlanChangePaymentMethod;
  /** Estável durante a tentativa: a repetição devolve o estado gravado, sem nova cobrança. */
  idempotencyKey: string;
}

export interface CancelPlanChangeRequest {
  idempotencyKey: string;
}

// --- Invoices (GET /billing/invoices) ---

export interface Invoice {
  id: string;
  amount: number;
  status: InvoiceStatus;
  dueDate: string;
  paidAt: string | null;
  paymentMethod: PaymentMethod;
  invoiceUrl: string | null;
  /** Ausente numa API anterior aos planos: a fatura é da assinatura. */
  kind?: InvoiceKind;
  /**
   * Atendimentos do pacote, só na fatura `ai_units` (change billing-ai-units).
   * Nulo nas outras e ausente numa API anterior a ele.
   */
  aiUnits?: number | null;
}

export interface InvoicesResponse {
  items: Invoice[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface InvoicesQueryParams {
  page?: number;
  pageSize?: number;
}

// --- Atendimentos do Atendente IA (change billing-ai-units) ---
//
// Os literais são os `[JsonStringEnumMemberName]` da API (contrato
// docs/atendente-ia.md §4.3), comparados por igualdade.

export type AiUnitBalanceState = 'ok' | 'low' | 'exhausted';

export type AiUnitLotSource = 'plan_cycle' | 'plan_change_proration' | 'purchase' | 'concession';

export type AiUnitEntryKind = 'grant' | 'consume' | 'expire' | 'revoke' | 'restore';

export type AiUnitPurchaseStatus = 'pending' | 'paid' | 'expired' | 'refunded' | 'charged_back';

/** Formas de pagamento de um pacote: sem boleto. */
export type AiUnitPaymentMethod = Extract<PaymentMethod, 'pix' | 'credit_card'>;

/** O aviso ativo da carteira: 80% e 100% do ciclo, ou sem saldo. */
export type AiUnitActiveAlert = 'cycle_80' | 'cycle_100' | 'exhausted';

/** A cota do ciclo vigente (`cycle` de `GET /billing/ai-units`). */
export interface AiUnitsCycle {
  used: number;
  total: number;
  startsAt: string;
  /** Fim exclusivo do ciclo: o instante em que a cota se renova ("renova em"). */
  endsAt: string;
}

/** O vencimento mais próximo entre os lotes com sobra. */
export interface AiUnitExpiry {
  at: string;
  units: number;
}

/** A previsão no ritmo dos últimos 7 dias. */
export interface AiUnitForecast {
  /** Dia (AAAA-MM-DD, São Paulo) em que o saldo acaba; nulo quando dura até a renovação. */
  exhaustsOn: string | null;
  dailyAverage: number;
}

/** Um lote vigente, na ordem em que a carteira o consome. */
export interface AiUnitLot {
  lotId: string;
  source: AiUnitLotSource;
  units: number;
  remainingUnits: number;
  grantedAt: string;
  expiresAt: string;
}

/** Uma categoria de mensagem da Meta no mês. */
export interface MetaCostCategoryEstimate {
  category: string;
  messages: number;
  usdPerMessage: number | null;
  estimatedUsd: number | null;
  estimatedBrl: number | null;
}

/** A estimativa do que a Meta cobra no mês; a cobrança oficial é da Meta. */
export interface MetaCostEstimate {
  /** `AAAA-MM`, no calendário de São Paulo. */
  month: string;
  billableMessages: number;
  /** Nulo quando nenhuma categoria com mensagem tem tarifa cadastrada. */
  estimatedUsd: number | null;
  estimatedBrl: number | null;
  categories: MetaCostCategoryEstimate[];
}

/** `GET /billing/ai-units`: o saldo de agora, sem identificador de conversa. */
export interface AiUnitsSummary {
  /** O direito vigente inclui o Atendente IA. Falso: nada da área aparece (D15). */
  hasAttendantPlan: boolean;
  state: AiUnitBalanceState;
  cycle: AiUnitsCycle | null;
  /** O que sobra em pacotes e cortesias vigentes. */
  packUnits: number;
  /** Atendimentos de carência ainda não descontados de nenhum lote. */
  graceDebt: number;
  nextExpiry: AiUnitExpiry | null;
  forecast: AiUnitForecast | null;
  lots: AiUnitLot[];
  /** Sempre nulo sem o plano. */
  alert: AiUnitActiveAlert | null;
  /** Nula sem leitura do canal WhatsApp. */
  metaEstimate: MetaCostEstimate | null;
  /**
   * A venda de pacotes está ligada no ambiente e o tenant tem o plano. Falso: nenhum
   * "Comprar atendimentos" aparece, nem para o dono (a compra responderia 404
   * `billing.ai_units_sales_disabled`). Quem compra continua decidido por `billing.write`.
   */
  canPurchase: boolean;
}

/** Uma linha do extrato (`GET /billing/ai-units/entries`). */
export interface AiUnitLedgerLine {
  /** Dia da linha (AAAA-MM-DD, São Paulo). */
  date: string;
  occurredAt: string;
  kind: AiUnitEntryKind;
  /** Com sinal: positiva na concessão e na devolução. */
  units: number;
  /** Descrição em pt-BR, escrita pela API ("Cota do mês", "23 atendimentos"). */
  description: string;
  /** Nula na carência, que não tem lote. */
  source: AiUnitLotSource | null;
  lotId: string | null;
  /** A linha soma o consumo e as devoluções do dia num lote. */
  aggregated: boolean;
}

/** Envelope de paginação padrão da API (`PagedResponse`). */
export interface AiUnitLedgerResponse {
  items: AiUnitLedgerLine[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export interface AiUnitLedgerQueryParams {
  page?: number;
  pageSize?: number;
}

/** Pix de uma compra que aguarda pagamento: o QR se desenha do copia-e-cola. */
export interface AiUnitPurchasePix {
  copyPaste: string;
  expiresAt: string | null;
}

/** Uma compra de pacote, com o retrato da versão vendida. */
export interface AiUnitPurchase {
  purchaseId: string;
  packCode: string;
  units: number;
  amount: number;
  validityMonths: number;
  paymentMethod: AiUnitPaymentMethod;
  status: AiUnitPurchaseStatus;
  /** Só enquanto a compra por Pix aguarda pagamento e o QR veio do gateway. */
  pix: AiUnitPurchasePix | null;
  /** Página de pagamento: o link do cartão, e a reserva do Pix sem QR. */
  invoiceUrl: string | null;
  createdAt: string;
  paidAt: string | null;
  expiredAt: string | null;
}

export interface CreateAiUnitPurchaseRequest {
  packCode: string;
  paymentMethod: AiUnitPaymentMethod;
  /** Só quando a API os pede (a loja ainda sem cadastro de cobrança). */
  billingInfo?: BillingInfo;
  /** Estável durante a tentativa: a repetição devolve a compra gravada, sem nova cobrança. */
  idempotencyKey: string;
}

export interface CreateAiUnitPurchaseResult {
  purchase: AiUnitPurchase;
  /** 201: a compra nasceu agora. 200: a repetição da mesma chave. */
  created: boolean;
}

export const AI_UNIT_SOURCE_LABELS: Record<AiUnitLotSource, string> = {
  plan_cycle: 'Cota do plano',
  plan_change_proration: 'Mudança de plano',
  purchase: 'Pacote',
  concession: 'Cortesia',
};

// --- Display helpers ---

export const BILLING_CYCLE_LABELS: Record<BillingCycle, string> = {
  monthly: 'Mensal',
  yearly: 'Anual',
};

/** Sufixo do preço por ciclo: "R$ 39,90/mês", "R$ 399,00/ano". */
export const BILLING_CYCLE_SUFFIX: Record<BillingCycle, string> = {
  monthly: '/mês',
  yearly: '/ano',
};

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  credit_card: 'Cartão de crédito',
  boleto: 'Boleto',
  pix: 'Pix',
};

export const SUBSCRIPTION_STATUS_LABELS: Record<SubscriptionStatus, string> = {
  none: 'Sem assinatura',
  pending: 'Pendente',
  active: 'Ativa',
  overdue: 'Vencida',
  cancelled: 'Cancelada',
};

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  paid: 'Paga',
  pending: 'Pendente',
  overdue: 'Vencida',
  refunded: 'Estornada',
  cancelled: 'Cancelada',
};

export const INVOICE_KIND_LABELS: Record<InvoiceKind, string> = {
  subscription: 'Assinatura',
  plan_change: 'Mudança de plano',
  ai_units: 'Pacote de atendimentos',
};

/**
 * Nome do plano quando a resposta não o traz: a cortesia e os direitos só
 * trazem o código, e o plano fora da venda não aparece em `plans[]`. Os nomes
 * são os do catálogo semeado pela API; o que a API manda sempre vence.
 */
export const KNOWN_PLAN_NAMES: Record<string, string> = {
  essential: 'Essencial',
  attendant: 'Vapt Menu + Atendente IA',
  trial: 'Teste grátis',
};

/** Descrição curta de cada recurso de plano, para dizer o que o plano inclui. */
export const PLAN_FEATURE_LABELS: Record<string, string> = {
  whatsapp_attendant: 'Atendente IA no WhatsApp',
};
